import { useState, useCallback, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, ShoppingBag, Minus, Plus, X, ZoomIn, Zap } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import OrderOfflineButton from "@/components/OrderOfflineButton";
import { useCart } from "@/contexts/CartContext";
import { useWishlist } from "@/contexts/WishlistContext";
import { toast } from "sonner";
import {
  getAllImages,
  getFirstImage,
  getEffectivePrice,
  type Product,
} from "@/hooks/useProducts";
import plymhairLogo from "@/assets/plymhair-logo.svg";
import cleanDescription from "@/lib/cleanDescription";
import { OUT_OF_STOCK_LABEL } from "@/lib/socialLinks";

interface QuickViewModalProps {
  product: Product | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ImageZoomOverlay = ({
  src,
  alt,
  onClose,
}: {
  src: string;
  alt: string;
  onClose: () => void;
}) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [lastPos, setLastPos] = useState({ x: 0, y: 0 });

  const handleDoubleClick = () => {
    if (scale > 1) {
      setScale(1);
      setPosition({ x: 0, y: 0 });
    } else {
      setScale(2.5);
    }
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.stopPropagation();
    const next = Math.min(4, Math.max(1, scale - e.deltaY * 0.002));
    setScale(next);
    if (next <= 1) setPosition({ x: 0, y: 0 });
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    if (scale <= 1) return;
    setDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    setLastPos(position);
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!dragging) return;
    setPosition({
      x: lastPos.x + (e.clientX - dragStart.x),
      y: lastPos.y + (e.clientY - dragStart.y),
    });
  };

  const handlePointerUp = () => setDragging(false);

  return (
    <div
      className="fixed inset-0 z-[100] bg-foreground/95 backdrop-blur-2xl flex items-center justify-center"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-[101] w-10 h-10 rounded-full bg-background/10 backdrop-blur-md flex items-center justify-center text-background/80 hover:text-background hover:bg-background/20 transition-all"
      >
        <X className="w-5 h-5" />
      </button>
      <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-background/40 text-[11px] font-body tracking-wide select-none">
        Double-tap to zoom · Scroll to zoom · Drag to pan
      </p>
      <div
        className="w-full h-full flex items-center justify-center overflow-hidden cursor-grab active:cursor-grabbing"
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={handleDoubleClick}
        onWheel={handleWheel}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        <img
          src={src}
          alt={alt}
          className="max-w-full max-h-full object-contain select-none transition-transform duration-150"
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
          }}
          draggable={false}
        />
      </div>
    </div>
  );
};

const EmblaGallery = ({
  images,
  fallback,
  alt,
  selectedImage,
  onSelect,
  onImageTap,
}: {
  images: string[];
  fallback: string;
  alt: string;
  selectedImage: number;
  onSelect: (idx: number) => void;
  onImageTap: (idx: number) => void;
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, startIndex: selectedImage });

  const onEmblaSelect = useCallback(() => {
    if (!emblaApi) return;
    onSelect(emblaApi.selectedScrollSnap());
  }, [emblaApi, onSelect]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", onEmblaSelect);
    return () => { emblaApi.off("select", onEmblaSelect); };
  }, [emblaApi, onEmblaSelect]);

  useEffect(() => {
    if (emblaApi && emblaApi.selectedScrollSnap() !== selectedImage) {
      emblaApi.scrollTo(selectedImage);
    }
  }, [emblaApi, selectedImage]);

  const displayImages = images.length > 0 ? images : [fallback];

  return (
    <>
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex">
          {displayImages.map((img, i) => (
            <div key={i} className="flex-[0_0_100%] min-w-0">
              <div
                className="aspect-[3/4] overflow-hidden cursor-zoom-in group/img relative"
                onClick={() => onImageTap(i)}
              >
                <img src={img} alt={`${alt} ${i + 1}`} className="w-full h-full object-cover transition-transform duration-500 group-hover/img:scale-[1.03]" />
                <div className="absolute inset-0 bg-foreground/0 group-hover/img:bg-foreground/5 transition-colors duration-300 flex items-center justify-center">
                  <ZoomIn className="w-6 h-6 text-background/0 group-hover/img:text-background/70 transition-all duration-300 drop-shadow-lg" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {displayImages.length > 1 && (
        <div className="flex justify-center gap-2 py-3">
          {displayImages.map((_, i) => (
            <button
              key={i}
              onClick={() => { onSelect(i); emblaApi?.scrollTo(i); }}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                selectedImage === i ? "bg-primary w-5" : "bg-muted-foreground/25 hover:bg-muted-foreground/40"
              }`}
            />
          ))}
        </div>
      )}
    </>
  );
};


const QuickViewModal = ({ product, open, onOpenChange }: QuickViewModalProps) => {
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedAttr, setSelectedAttr] = useState("");
  const [qty, setQty] = useState(1);
  const [zoomImage, setZoomImage] = useState<string | null>(null);
  const { addItem } = useCart();
  const { isInWishlist, toggleWishlist } = useWishlist();
  const navigate = useNavigate();

  if (!product) return null;

  const images = getAllImages(product.images);
  const displayImages = images.length > 0 ? images : [getFirstImage(product.images)];
  const price = getEffectivePrice(product);
  const hasDiscount =
    product.sale_price &&
    product.regular_price &&
    product.sale_price < product.regular_price;

  const attrOptions = product.attribute_1_values
    ? product.attribute_1_values
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean)
    : [];

  const handleAdd = () => {
    addItem(product, qty, selectedAttr || undefined);
    toast.success(`${product.name} added to cart`);
    onOpenChange(false);
    setQty(1);
    setSelectedAttr("");
    setSelectedImage(0);
  };

  const handleBuyNow = () => {
    addItem(product, qty, selectedAttr || undefined);
    onOpenChange(false);
    setQty(1);
    setSelectedAttr("");
    setSelectedImage(0);
    navigate("/checkout");
  };

  const discountPercent = hasDiscount
    ? Math.round(((product.regular_price! - product.sale_price!) / product.regular_price!) * 100)
    : 0;

  return (
    <>
      {zoomImage && (
        <ImageZoomOverlay
          src={zoomImage}
          alt={product.name}
          onClose={() => setZoomImage(null)}
        />
      )}
      <Dialog open={open} onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) {
          setQty(1);
          setSelectedAttr("");
          setSelectedImage(0);
        }
      }}>
        <DialogContent className="max-w-3xl p-0 gap-0 overflow-hidden max-h-[90vh] overflow-y-auto sm:max-h-[85vh] border-border/50 bg-background/95 backdrop-blur-2xl shadow-2xl rounded-xl">
          <DialogTitle className="sr-only">{product.name}</DialogTitle>
          <div className="grid grid-cols-1 md:grid-cols-2">
            {/* Images */}
            <div className="bg-secondary/30 relative">
              <div className="absolute top-2.5 left-2.5 z-10 w-8 h-8 rounded-full bg-background/80 backdrop-blur-sm flex items-center justify-center shadow-sm">
                <img src={plymhairLogo} alt="Plymhair logo" className="w-5 h-5 object-contain" />
              </div>
              <EmblaGallery
                images={images}
                fallback={getFirstImage(product.images)}
                alt={product.name}
                selectedImage={selectedImage}
                onSelect={setSelectedImage}
                onImageTap={(idx) => setZoomImage(displayImages[idx])}
              />

              {/* Sale badge */}
              {hasDiscount && (
                <div className="absolute top-3 left-3 bg-primary text-primary-foreground text-[10px] font-body font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full">
                  −{discountPercent}%
                </div>
              )}
            </div>

            {/* Info */}
            <div className="p-5 md:p-7 flex flex-col">
              {product.categories && (
                <p className="text-[10px] text-primary font-body font-medium uppercase tracking-[0.2em] mb-2">
                  {product.categories.split(",")[0]?.split(">").pop()?.trim()}
                </p>
              )}
              <h2 className="font-display text-xl md:text-2xl font-bold mb-4 leading-tight">
                {product.name}
              </h2>

              {/* Price */}
              <div className="flex items-baseline gap-3 mb-5">
                <span className="text-2xl font-bold font-display text-foreground">
                  £{price.toFixed(2)}
                </span>
                {hasDiscount && (
                  <span className="text-sm text-muted-foreground/60 line-through font-body">
                    £{product.regular_price?.toFixed(2)}
                  </span>
                )}
              </div>

              {product.short_description && (
                <p className="text-sm text-muted-foreground font-body leading-relaxed mb-5 line-clamp-3">
                  {cleanDescription(product.short_description)}
                </p>
              )}

              <div className="w-full h-px bg-border/60 mb-5" />

              {/* Attribute selector */}
              {product.type === "variable" && attrOptions.length > 0 && (
                <div className="mb-5">
                  <label className="text-[11px] font-body font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">
                    {product.attribute_1_name || "Option"}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {attrOptions.map((opt) => (
                      <button
                        key={opt}
                        onClick={() => setSelectedAttr(selectedAttr === opt ? "" : opt)}
                        className={`px-3.5 py-1.5 rounded-full text-xs font-body font-medium transition-all duration-200 border ${
                          selectedAttr === opt
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-border bg-background hover:border-primary/40 hover:bg-secondary"
                        }`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Quantity */}
              <div className="mb-5">
                <label className="text-[11px] font-body font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">
                  Quantity
                </label>
                <div className="flex items-center border border-border rounded-full w-fit bg-secondary/50">
                  <button
                    onClick={() => setQty(Math.max(1, qty - 1))}
                    className="w-9 h-9 flex items-center justify-center hover:bg-secondary rounded-l-full transition-colors"
                  >
                    <Minus className="w-3 h-3" />
                  </button>
                  <span className="w-10 text-center font-body text-sm font-semibold">
                    {qty}
                  </span>
                  <button
                    onClick={() => setQty(qty + 1)}
                    className="w-9 h-9 flex items-center justify-center hover:bg-secondary rounded-r-full transition-colors"
                  >
                    <Plus className="w-3 h-3" />
                  </button>
                </div>
              </div>

              {/* Stock */}
              <div className="flex items-center gap-2 mb-5">
                <span
                  className={`w-1.5 h-1.5 rounded-full ${
                    product.in_stock ? "bg-green-500" : "bg-destructive"
                  }`}
                />
                <span className="text-xs font-body text-muted-foreground">
                  {product.in_stock ? "In Stock" : OUT_OF_STOCK_LABEL}
                </span>
              </div>

              {/* Actions */}
              <div className="flex flex-col gap-2 mt-auto">
                {product.in_stock ? (
                  <>
                    <div className="flex gap-2">
                      <button
                        className="flex-1 flex items-center justify-center gap-2 bg-foreground text-background font-body text-sm font-semibold uppercase tracking-wider py-3 rounded-full hover:bg-foreground/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        disabled={product.type === "variable" && !selectedAttr}
                        onClick={handleAdd}
                      >
                        <ShoppingBag className="w-4 h-4" />
                        Add to Cart
                      </button>
                      <button
                        onClick={() => toggleWishlist(product.id)}
                        className={`w-12 h-12 rounded-full border flex items-center justify-center transition-all duration-200 ${
                          isInWishlist(product.id)
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-border hover:border-primary/40 text-muted-foreground hover:text-primary"
                        }`}
                      >
                        <Heart
                          className={`w-4 h-4 ${
                            isInWishlist(product.id) ? "fill-current" : ""
                          }`}
                        />
                      </button>
                    </div>
                    <button
                      className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground font-body text-sm font-semibold uppercase tracking-wider py-3 rounded-full hover:bg-primary/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      disabled={product.type === "variable" && !selectedAttr}
                      onClick={handleBuyNow}
                    >
                      <Zap className="w-4 h-4" />
                      Buy Now
                    </button>
                  </>
                ) : (
                  <OrderOfflineButton productName={product.name} productSku={product.sku} productUrl={typeof window !== "undefined" ? `${window.location.origin}/product/${product.slug}` : undefined} />
                )}
              </div>

              <Link
                to={`/product/${product.slug}`}
                className="text-xs text-primary hover:underline font-body mt-4 text-center tracking-wide"
                onClick={() => onOpenChange(false)}
              >
                View full details →
              </Link>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default QuickViewModal;